import { useState, useMemo, useCallback, useContext } from "react";
import { Product } from "../../../../interfaces/sales";
import { partialCancelSaleService } from "../../../../services/sales";
import { getServiceError } from "../../../../utils/getServiceError";
import { ErrorModalContext } from "../../../../components/AlertsModal/AlertsModal";
import {
  PartialCancelStep,
  SalePayment,
} from "../interfaces/partialCancellation";

interface UsePartialCancellationProps {
  products: Product[];
  payments: SalePayment[];
  saleUuid: string;
  saleCode: string;
  onSuccess: () => void;
  closeModal: () => void;
}

// Payments with status Refunded (4) or RefundPending (3) are not refundable
const isRefundablePayment = (status: number | string): boolean => {
  const s = Number(status);
  return s !== 3 && s !== 4;
};

// Merchants whose refund phone the backend resolves from the original charge (the customer
// who paid). For these the operator must NOT be asked to type a CLABE-request phone.
const BACKEND_RESOLVED_PHONE_MERCHANTS = ["boletomovil"];

export const usePartialCancellation = ({
  products,
  payments: allPayments,
  saleUuid,
  saleCode,
  onSuccess,
  closeModal,
}: UsePartialCancellationProps) => {
  const { showAlert } = useContext(ErrorModalContext);
  const payments = useMemo(
    () => allPayments.filter((p) => isRefundablePayment(p.status)),
    [allPayments],
  );
  const [step, setStep] = useState<PartialCancelStep>("products");
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, number>
  >({});
  const [paymentRefunds, setPaymentRefunds] = useState<
    Record<string, number>
  >({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneMatch, setPhoneMatch] = useState(false);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Per-product restock decisions, keyed by uuid_sale_product. Defaults to `true` (regresar al stock)
  // when missing, matching the backend's default when the flag is not sent.
  const [restockDecisions, setRestockDecisions] = useState<
    Record<string, boolean>
  >({});

  /**
   * Cambia la decisión de reinventario para un uuid_sale_product específico en la cancelación parcial.
   */
  const setRestockDecision = useCallback(
    (uuid: string, restock: boolean): void => {
      setRestockDecisions((prev) => ({ ...prev, [uuid]: restock }));
    },
    [],
  );

  const refundTotal = useMemo(() => {
    return Object.entries(selectedProducts).reduce(
      (total, [uuid, cancelQty]) => {
        if (cancelQty <= 0) return total;
        const product = products.find((p) => p.uuid === uuid);
        if (!product) return total;
        return total + product.price * cancelQty;
      },
      0,
    );
  }, [selectedProducts, products]);

  const isAllSelected = useMemo(() => {
    const activeProducts = products.filter((p) => p.quantity > 0);
    if (activeProducts.length === 0) return false;
    return activeProducts.every(
      (p) => (selectedProducts[p.uuid] || 0) >= p.quantity,
    );
  }, [selectedProducts, products]);

  const hasSelectedProducts = useMemo(
    () => Object.values(selectedProducts).some((qty) => qty > 0),
    [selectedProducts],
  );

  // A typed phone is needed only for card refunds whose merchant does NOT self-resolve the
  // customer phone (e.g. bankaool/clover). boletomovil refunds skip the phone step.
  const requiresPhoneNumber = useMemo(() => {
    const needsTypedPhone = (payment?: SalePayment): boolean =>
      !!payment?.card_transaction &&
      !BACKEND_RESOLVED_PHONE_MERCHANTS.includes(
        payment.card_transaction.merchant?.toLowerCase() ?? "",
      );
    if (payments.length <= 1) {
      // Single payment: check if it's a card payment needing a typed phone and products are selected
      return hasSelectedProducts && needsTypedPhone(payments[0]);
    }
    // Multi-payment: check if any such card payment has a refund amount > 0
    return Object.entries(paymentRefunds).some(([uuid, amount]) => {
      if (amount <= 0) return false;
      const payment = payments.find((p) => p.uuid === uuid);
      return needsTypedPhone(payment);
    });
  }, [payments, paymentRefunds, hasSelectedProducts]);

  const isPhoneNumberValid = useMemo(
    () => phoneNumber.length >= 10 && phoneMatch,
    [phoneNumber, phoneMatch],
  );

  const isPaymentDistributionValid = useMemo(() => {
    if (payments.length <= 1) return true;
    const totalDistributed = Math.round(
      Object.values(paymentRefunds).reduce((sum, amount) => sum + (amount || 0), 0) * 100
    ) / 100;
    return Math.abs(totalDistributed - refundTotal) < 0.01;
  }, [paymentRefunds, refundTotal, payments]);

  const handleProductSelection = useCallback(
    (uuid: string, quantity: number) => {
      const product = products.find((p) => p.uuid === uuid);
      if (!product) return;

      const clampedQty = Math.max(0, Math.min(quantity, product.quantity));

      setSelectedProducts((prev) => {
        const next = { ...prev };

        // Block manual changes to addons — they are auto-managed by parent
        if (product.uuid_parent) {
          return prev;
        }

        if (clampedQty === 0) {
          delete next[uuid];
        } else {
          next[uuid] = clampedQty;
        }

        // Auto-adjust addon quantities proportionally to parent cancel qty
        const addons = products.filter(
          (p) => p.uuid_parent === product.uuid,
        );
        addons.forEach((addon) => {
          const addonPerUnit = addon.quantity / product.quantity;
          const addonCancelQty = Math.floor(clampedQty * addonPerUnit);
          if (addonCancelQty === 0) {
            delete next[addon.uuid];
          } else {
            next[addon.uuid] = addonCancelQty;
          }
        });

        return next;
      });
    },
    [products],
  );

  const handlePaymentRefund = useCallback(
    (uuid: string, amount: number) => {
      setPaymentRefunds((prev) => ({
        ...prev,
        [uuid]: amount,
      }));
    },
    [],
  );

  const goToNextStep = useCallback(() => {
    if (step === "products") {
      if (!hasSelectedProducts) {
        return;
      }
      // Always insert the restock step right after product selection so the operator picks
      // a Sí/No decision per item before continuing to payments/phone/comment.
      setStep("restock");
    } else if (step === "restock") {
      if (payments.length <= 1) {
        // Single payment: skip payment step, check if phone step needed
        if (requiresPhoneNumber) {
          setStep("phone");
        } else {
          setStep("comment");
        }
      } else {
        const initialRefunds: Record<string, number> = {};
        payments.forEach((p) => {
          initialRefunds[p.uuid] = paymentRefunds[p.uuid] || 0;
        });
        setPaymentRefunds(initialRefunds);
        setStep("payments");
      }
    } else if (step === "payments") {
      if (!isPaymentDistributionValid) {
        return;
      }
      if (requiresPhoneNumber) {
        setStep("phone");
      } else {
        setStep("comment");
      }
    } else if (step === "phone") {
      if (!isPhoneNumberValid) {
        return;
      }
      setStep("comment");
    } else if (step === "comment") {
      setStep("confirm");
    }
  }, [
    step,
    hasSelectedProducts,
    payments,
    paymentRefunds,
    isPaymentDistributionValid,
    requiresPhoneNumber,
    isPhoneNumberValid,
  ]);

  const goToPreviousStep = useCallback(() => {
    if (step === "confirm") {
      setStep("comment");
    } else if (step === "comment") {
      if (requiresPhoneNumber) {
        setStep("phone");
      } else if (payments.length > 1) {
        setStep("payments");
      } else {
        setStep("restock");
      }
    } else if (step === "phone") {
      if (payments.length <= 1) {
        setStep("restock");
      } else {
        setStep("payments");
      }
    } else if (step === "payments") {
      setStep("restock");
    } else if (step === "restock") {
      setStep("products");
    }
  }, [step, payments, requiresPhoneNumber]);

  const submit = useCallback(async () => {
    try {
      setIsLoading(true);

      const productsToCancel = Object.entries(selectedProducts)
        .filter(([, qty]) => qty > 0)
        .map(([uuid_sale_product, quantity]) => ({
          uuid_sale_product,
          quantity,
          // Backend reads the flag only on Dashboard requests; default `true` mirrors legacy behavior.
          restock: restockDecisions[uuid_sale_product] !== false,
        }));

      const paymentsDistribution =
        payments.length > 1
          ? Object.entries(paymentRefunds)
              .filter(([, amount]) => amount > 0)
              .map(([uuid, refund_amount]) => ({ uuid, refund_amount }))
          : undefined;

      const result = await partialCancelSaleService(saleUuid, {
        products: productsToCancel,
        payments: paymentsDistribution,
        phone_number: requiresPhoneNumber ? phoneNumber : undefined,
        comment,
      });

      setIsLoading(false);
      closeModal();
      if (result?.status === 'pending') {
        showAlert(
          "Solicitud de cancelación enviada. Un aprobador debe revisarla antes de ejecutarse.",
          "info",
          "Cancelación pendiente de aprobación",
        );
      } else {
        showAlert(
          `La venta ${saleCode} ha sido parcialmente cancelada.`,
          "success",
          "Cancelación parcial exitosa",
        );
        onSuccess();
      }
    } catch (error) {
      setIsLoading(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  }, [
    selectedProducts,
    paymentRefunds,
    payments,
    saleUuid,
    saleCode,
    closeModal,
    showAlert,
    onSuccess,
    requiresPhoneNumber,
    phoneNumber,
    comment,
    restockDecisions,
  ]);

  const reset = useCallback(() => {
    setStep("products");
    setSelectedProducts({});
    setPaymentRefunds({});
    setPhoneNumber("");
    setComment("");
    setIsLoading(false);
    setRestockDecisions({});
  }, []);

  return {
    step,
    selectedProducts,
    payments,
    paymentRefunds,
    phoneNumber,
    comment,
    refundTotal,
    isLoading,
    isAllSelected,
    hasSelectedProducts,
    requiresPhoneNumber,
    isPhoneNumberValid,
    isPaymentDistributionValid,
    handleProductSelection,
    handlePaymentRefund,
    setPhoneNumber,
    setPhoneMatch,
    setComment,
    goToNextStep,
    goToPreviousStep,
    submit,
    reset,
    restockDecisions,
    setRestockDecision,
  };
};
